import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bot, Send, Loader2, Wrench, ShieldAlert, ChevronDown, ChevronRight, Sparkles, User, RefreshCw, ArrowUpRight } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { titleCase } from "@/lib/format";
import { PageHeader } from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

const KIND_LABEL = {
  rapid_response: "Threat in the feed",
  trending_issue: "Trending issue",
  money: "Opponent money",
  geography: "Sentiment map",
  theme: "Message pillar",
  generic: "Suggested",
};

export default function AgentConsole() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const { data: suggested, isFetching, refetch } = useQuery({
    queryKey: ["agentSuggestions"],
    queryFn: () => api.get("/agent/suggestions").then((r) => r.data.suggestions),
  });

  // Block body on purpose: a concise arrow would implicitly return whatever the expression
  // yields, and React 19 crashes with "destroy is not a function" if an effect returns a
  // non-function. This is the only effect in the app that could ever leak a return value.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const prompt = (text ?? input).trim();
    if (!prompt || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: prompt }]);
    setLoading(true);
    try {
      const { data } = await api.post("/agent/run", { prompt });
      setMessages((m) => [...m, { role: "agent", run: data }]);
    } catch (e) {
      toast.error(apiError(e, "Agent failed"));
      setMessages((m) => [...m, { role: "agent", error: apiError(e) }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-9rem)] flex-col">
      <PageHeader
        title="Agent Console"
        subtitle="Drive the platform in plain English. The agent plans, calls tools, and returns drafts for your approval. It never sends or publishes on its own."
        icon={Bot}
      />

      <div className="flex-1 overflow-y-auto rounded-xl border border-border bg-white p-4 shadow-sm sm:p-6">
        {messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-red/10 text-brand-red">
              <Sparkles className="h-6 w-6" />
            </div>
            <h3 className="font-heading text-xl font-bold text-brand-ink">What should we tackle?</h3>
            <p className="mt-1 max-w-md text-sm text-brand-mute">
              Pulled from what is in your feed right now. Tap one and the agent plans and acts.
            </p>
            <div className="mt-6 grid w-full max-w-3xl gap-2 sm:grid-cols-2">
              {(suggested || []).map((s, i) => (
                <div
                  key={s.prompt}
                  data-testid={`agent-suggestion-${i}`}
                  className="group flex flex-col rounded-lg border border-border bg-white p-3 text-left transition-all hover:-translate-y-0.5 hover:border-brand-red hover:shadow-sm"
                  style={{ animation: `fade-in 0.4s ease both`, animationDelay: `${i * 70}ms` }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[0.65rem] font-bold uppercase tracking-wider text-brand-red">
                      {KIND_LABEL[s.kind] || KIND_LABEL.generic}
                    </span>
                    {s.link && (s.link.startsWith("http") ? (
                      <a href={s.link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                         className="text-brand-mute transition-colors hover:text-brand-red"
                         data-testid={`agent-suggestion-${i}-source`} title="Open the source">
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </a>
                    ) : (
                      <Link to={s.link} onClick={(e) => e.stopPropagation()}
                            className="text-brand-mute transition-colors hover:text-brand-red"
                            data-testid={`agent-suggestion-${i}-source`} title="Open in the app">
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </Link>
                    ))}
                  </div>
                  <button
                    onClick={() => send(s.prompt)}
                    className="mt-1.5 text-left text-sm text-brand-ink"
                    data-testid={`agent-suggestion-${i}-run`}
                  >
                    {s.prompt}
                  </button>
                </div>
              ))}
            </div>
            <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}
                    className="mt-4 text-xs text-brand-mute" data-testid="refresh-suggestions-btn">
              {isFetching ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />}
              Refresh from the feed
            </Button>
          </div>
        )}

        <div className="space-y-5">
          {messages.map((m, i) =>
            m.role === "user" ? (
              <div key={i} className="flex justify-end">
                <div className="flex max-w-[80%] items-start gap-2.5">
                  <div className="rounded-2xl rounded-tr-sm bg-brand-navy px-4 py-2.5 text-sm text-white">{m.text}</div>
                  <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-navy text-white"><User className="h-3.5 w-3.5" /></div>
                </div>
              </div>
            ) : (
              <AgentMessage key={i} m={m} />
            )
          )}
          {loading && (
            <div className="flex items-center gap-2.5">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-red text-white"><Bot className="h-3.5 w-3.5" /></div>
              <div className="flex items-center gap-2 rounded-2xl rounded-tl-sm border border-border bg-white px-4 py-2.5 text-sm text-brand-mute">
                <Loader2 className="h-4 w-4 animate-spin text-brand-red" /> Planning & calling tools…
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
      </div>

      <div className="mt-3 flex items-end gap-2 rounded-xl border border-border bg-white p-2 shadow-sm">
        <Textarea
          data-testid="agent-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Ask the agent to find signals, draft content, build a segment…"
          rows={1}
          className="min-h-[44px] resize-none border-0 focus-visible:ring-0"
        />
        <Button data-testid="agent-send-btn" onClick={() => send()} disabled={loading || !input.trim()} className="h-11 bg-brand-red text-white hover:bg-brand-redhover">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function AgentMessage({ m }) {
  const [open, setOpen] = useState(false);
  if (m.error) {
    return (
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-red text-white"><Bot className="h-3.5 w-3.5" /></div>
        <div className="rounded-2xl rounded-tl-sm border border-neg-border bg-neg-bg px-4 py-2.5 text-sm text-neg-text">{m.error}</div>
      </div>
    );
  }
  const run = m.run;
  return (
    <div className="flex items-start gap-2.5">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-red text-white"><Bot className="h-3.5 w-3.5" /></div>
      <div className="max-w-[85%] space-y-3">
        <div className="rounded-2xl rounded-tl-sm border border-border bg-white px-4 py-3 text-sm leading-relaxed text-brand-ink shadow-sm">
          <pre className="whitespace-pre-wrap font-body">{run.final_response}</pre>
        </div>

        {run.approvals?.length > 0 && (
          <div className="rounded-xl border border-neu-border bg-neu-bg px-4 py-3">
            <p className="flex items-center gap-1.5 text-xs font-bold text-neu-text"><ShieldAlert className="h-3.5 w-3.5" /> Awaiting your approval</p>
            <div className="mt-2 space-y-1.5">
              {run.approvals.map((a, i) => (
                <div key={i} className="flex items-center justify-between gap-2 text-sm text-brand-ink">
                  <span>{titleCase(a.tool)}{a.result?.preview ? `: "${a.result.preview.slice(0, 50)}…"` : ""}</span>
                  <Link to={a.tool === "create_trigger" ? "/automations" : "/studio"} className="text-xs font-semibold text-brand-red hover:underline">Review →</Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {run.steps?.length > 0 && (
          <div className="rounded-xl border border-border bg-slate-50/60">
            <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between px-4 py-2.5 text-xs font-semibold text-brand-navy">
              <span className="flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5" /> {run.steps.length} tool call{run.steps.length === 1 ? "" : "s"} · plan</span>
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            {open && (
              <div className="space-y-2 border-t border-border px-4 py-3">
                <p className="text-xs italic text-brand-mute">{run.plan}</p>
                {run.steps.map((s, i) => (
                  <div key={i} className="rounded-lg border border-border bg-white p-2.5">
                    <Badge variant="outline" className="mb-1 font-mono text-[0.65rem] text-brand-navy">{s.tool}</Badge>
                    <pre className="overflow-x-auto whitespace-pre-wrap text-[0.7rem] text-brand-mute">{JSON.stringify(s.result, null, 0).slice(0, 280)}</pre>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
